import type {
	ConstantsMfaSetupStruct,
	ConstantsMfaVerifyStruct,
} from "@gosaas/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	IconActivity,
	IconAlertCircle,
	IconCheck,
	IconClipboard,
	IconClock,
	IconDownload,
	IconLock,
	IconSend,
	IconShield,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { z } from "zod";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useAuth } from "@/components/providers/auth";
import { useFetchUserAuditLogs } from "@/components/queries/security";
import { ActiveSessionsCard } from "@/components/security/active-sessions";
import { auditLogColumns } from "@/components/tables/columns";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { copyToClipboard as copyTextToClipboard } from "@/utils/clipboard";

export function logMfaDisableError(
	errorMessage: string,
	isDev: boolean = import.meta.env.DEV,
): void {
	if (isDev) {
		console.error("Error disabling MFA:", errorMessage);
	}
}

export function Security() {
	const { user, refresh } = useAuth();
	const [mfaStep, setMfaStep] = useState<"initial" | "verify" | "recovery">(
		"initial",
	);
	const [mfaSecret, setMfaSecret] = useState<string>("");
	const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
	const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
	const [copyIcon, setCopyIcon] = useState<"copy" | "check">("copy");
	const [hasConfirmedBackup, setHasConfirmedBackup] = useState(false);

	const { auditLogs, isLoadingAuditLogs, refetchAuditLogs } =
		useFetchUserAuditLogs();

	const verifyMFASchema = z.object({
		otp: z.string().length(6, "Please enter a 6-digit code"),
	});

	const form = useForm<z.infer<typeof verifyMFASchema>>({
		resolver: zodResolver(verifyMFASchema),
		defaultValues: {
			otp: "",
		},
	});

	if (isLoadingAuditLogs) {
		return <Loader />;
	}

	const setupMFA = async () => {
		try {
			const response = await requestWithHandling<ConstantsMfaSetupStruct>(() =>
				apiClient.api.userMfaSetupCreate(),
			);
			const responseData = response.data;
			if (responseData) {
				setMfaSecret(responseData.secret_key ?? "");
				setQrCodeUrl(responseData.otp_key ?? "");
				setMfaStep("verify");
			} else {
				toast.error("Couldn't set up two-factor authentication", {
					description: "Received an unexpected response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't set up two-factor authentication", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const onSubmit = async (data: z.infer<typeof verifyMFASchema>) => {
		try {
			const response = await requestWithHandling<ConstantsMfaVerifyStruct>(() =>
				apiClient.api.userMfaSetupVerifyCreate({ token: data.otp }),
			);
			const responseData = response.data;
			if (responseData) {
				setRecoveryCodes(responseData?.recovery_codes ?? []);
				setMfaStep("recovery");
				toast.success("MFA setup successful");
				await refetchAuditLogs();
			} else {
				toast.error("Couldn't verify the code", {
					description: "Received an unexpected response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't verify the code", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const copyToClipboard = async (text: string) => {
		await copyTextToClipboard(text);
		setCopyIcon("check");
		setTimeout(() => setCopyIcon("copy"), 1000);
	};

	const downloadRecoveryCodes = () => {
		const element = document.createElement("a");
		const file = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
		element.href = URL.createObjectURL(file);
		element.download = "mfa-recovery-codes.txt";
		document.body.appendChild(element);
		element.click();
		document.body.removeChild(element);
	};

	const handleDisableMFA = async (data: z.infer<typeof verifyMFASchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userMfaDisableCreate({ token: data.otp }),
			);
			toast.success("MFA disabled successfully");
			await refresh();
			await refetchAuditLogs();
		} catch (error) {
			// Only log error message, not full error object to prevent sensitive data exposure
			const errorMessage =
				error instanceof TypedClientApiError
					? error.message
					: "An unknown error occurred";
			logMfaDisableError(errorMessage);
		}
	};

	const handleDialogClose = () => {
		if (!hasConfirmedBackup && mfaStep === "recovery") {
			toast.error("Back up your recovery codes first");
			return false;
		}
		setMfaStep("initial");
		setHasConfirmedBackup(false);
		return true;
	};

	const totalLogs = auditLogs?.length ?? 0;
	const loginAttempts =
		auditLogs?.filter((log) => log.action?.includes("LOGIN")).length ?? 0;
	const failedLoginAttempts =
		auditLogs?.filter((log) => log.action?.includes("FAILED")).length ?? 0;
	const uniqueIPs = new Set(auditLogs?.map((log) => log.ip_address) ?? []).size;

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "User", href: null },
					{ title: "Security", href: "/user/security" },
				]}
				title="Security"
				description="Manage your account security and audit logs"
				icon={<IconLock className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center gap-2">
						<IconLock className="h-5 w-5" />
						Multi-Factor Authentication (MFA)
					</CardTitle>
					<CardDescription>
						Enhance your account security by enabling two-factor authentication.
						This adds an extra layer of protection by requiring a code from your
						authenticator app when signing in.
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					{/* existing MFA setup/disable content unchanged */}
					{!user?.mfaEnabled && (
						<Dialog
							open={mfaStep !== "initial"}
							onOpenChange={async (open) => {
								if (!open) {
									const canClose = handleDialogClose();
									if (canClose) {
										if (mfaStep === "recovery" && hasConfirmedBackup) {
											await refresh();
											await refetchAuditLogs();
										}
										setMfaStep("initial");
									}
								} else {
									setupMFA();
								}
							}}
						>
							<DialogTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none bg-primary text-primary-foreground [a]:hover:bg-primary/80 h-8 gap-1.5 px-2.5">
								Setup MFA
							</DialogTrigger>
							<DialogContent className="sm:max-w-md">
								<DialogHeader>
									<DialogTitle>
										{mfaStep === "recovery" ? "Backup Codes" : "Setup MFA"}
									</DialogTitle>
									<DialogDescription>
										{mfaStep === "recovery"
											? "Download your backup codes to a safe location. These codes can be used to access your account if you lose your authenticator app."
											: "Follow the steps to enable multi-factor authentication"}
									</DialogDescription>
								</DialogHeader>
								{mfaStep === "verify" && (
									<div className="space-y-4">
										<div className="flex justify-center">
											{qrCodeUrl && <QRCode size={256} value={qrCodeUrl} />}
										</div>
										<div className="flex items-center justify-center space-x-2">
											<Input
												readOnly
												value={mfaSecret}
												className="font-mono max-w-xs"
											/>
											<Button
												variant="outline"
												size="icon"
												onClick={() => copyToClipboard(mfaSecret)}
											>
												{copyIcon === "copy" ? (
													<IconClipboard className="h-4 w-4" />
												) : (
													<IconCheck className="h-4 w-4" />
												)}
											</Button>
										</div>
										<Form {...form}>
											<form
												onSubmit={form.handleSubmit(onSubmit)}
												className="flex items-center justify-center space-x-4"
											>
												<FormField
													control={form.control}
													name="otp"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<InputOTP
																	maxLength={6}
																	value={field.value}
																	onChange={(value) => field.onChange(value)}
																>
																	<InputOTPGroup>
																		<InputOTPSlot index={0} />
																		<InputOTPSlot index={1} />
																		<InputOTPSlot index={2} />
																	</InputOTPGroup>
																	<InputOTPSeparator />
																	<InputOTPGroup>
																		<InputOTPSlot index={3} />
																		<InputOTPSlot index={4} />
																		<InputOTPSlot index={5} />
																	</InputOTPGroup>
																</InputOTP>
															</FormControl>
														</FormItem>
													)}
												/>
												<Button
													type="submit"
													disabled={form.formState.isSubmitting}
													size="icon"
												>
													<IconSend className="h-4 w-4" />
												</Button>
											</form>
										</Form>
									</div>
								)}

								{mfaStep === "recovery" && (
									<div className="space-y-4">
										<div className="bg-muted p-4 rounded-md">
											<pre className="font-mono text-sm">
												{recoveryCodes.join("\n")}
											</pre>
										</div>
										<div className="flex space-x-2">
											<Button
												variant="outline"
												onClick={() =>
													copyToClipboard(recoveryCodes.join("\n"))
												}
											>
												{copyIcon === "copy" ? (
													<IconClipboard className="h-4 w-4 mr-2" />
												) : (
													<IconCheck className="h-4 w-4 mr-2" />
												)}
												Copy
											</Button>
											<Button variant="outline" onClick={downloadRecoveryCodes}>
												<IconDownload className="h-4 w-4 mr-2" />
												Download
											</Button>
										</div>
										<div className="flex items-center space-x-2 pt-4">
											<Input
												type="checkbox"
												id="confirmBackup"
												className="h-4 w-4 rounded border-border"
												checked={hasConfirmedBackup}
												onChange={(e) =>
													setHasConfirmedBackup(e.target.checked)
												}
											/>
											<Label htmlFor="confirmBackup" className="text-sm">
												I have backed up or downloaded these recovery codes
											</Label>
										</div>
										<p className="text-sm text-muted-foreground">
											You must confirm that you have saved these codes before
											closing this dialog. You won't be able to see these codes
											again.
										</p>
									</div>
								)}
							</DialogContent>
						</Dialog>
					)}
					{user?.mfaEnabled === true && (
						<Dialog>
							<DialogTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30 h-8 gap-1.5 px-2.5">
								Disable MFA
							</DialogTrigger>
							<DialogContent className="sm:max-w-md">
								<DialogHeader>
									<DialogTitle>Disable MFA</DialogTitle>
									<DialogDescription>
										Please enter your authenticator code to disable MFA. This
										will remove the additional security from your account.
									</DialogDescription>
								</DialogHeader>
								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(handleDisableMFA)}
										className="flex items-center justify-center space-x-4"
									>
										<FormField
											control={form.control}
											name="otp"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<InputOTP
															maxLength={6}
															value={field.value}
															onChange={(value) => field.onChange(value)}
														>
															<InputOTPGroup>
																<InputOTPSlot index={0} />
																<InputOTPSlot index={1} />
																<InputOTPSlot index={2} />
															</InputOTPGroup>
															<InputOTPSeparator />
															<InputOTPGroup>
																<InputOTPSlot index={3} />
																<InputOTPSlot index={4} />
																<InputOTPSlot index={5} />
															</InputOTPGroup>
														</InputOTP>
													</FormControl>
												</FormItem>
											)}
										/>
										<Button
											type="submit"
											variant="destructive"
											disabled={form.formState.isSubmitting}
											size="icon"
										>
											<IconSend className="h-4 w-4" />
										</Button>
									</form>
								</Form>
							</DialogContent>
						</Dialog>
					)}
				</CardContent>
			</Card>

			<ActiveSessionsCard />

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-primary/10 rounded-lg">
								<IconActivity className="h-5 w-5 text-primary" />
							</div>
							<div>
								<p className="text-2xl font-bold">{totalLogs}</p>
								<p className="text-sm text-muted-foreground">Total Events</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-green-500/10 rounded-lg">
								<IconShield className="h-5 w-5 text-green-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{loginAttempts}</p>
								<p className="text-sm text-muted-foreground">Login Events</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-red-500/10 rounded-lg">
								<IconAlertCircle className="h-5 w-5 text-red-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{failedLoginAttempts}</p>
								<p className="text-sm text-muted-foreground">Failed Actions</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-orange-500/10 rounded-lg">
								<IconClock className="h-5 w-5 text-orange-600" />
							</div>
							<div>
								<p className="text-2xl font-bold">{uniqueIPs}</p>
								<p className="text-sm text-muted-foreground">Unique IPs</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center gap-2">
						<IconShield className="h-5 w-5" />
						Audit Log History
					</CardTitle>
					<CardDescription>
						Comprehensive log of all security events and user activities. Click
						on user agent entries to view detailed browser information.
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					<DataTable
						columns={auditLogColumns}
						data={auditLogs ?? []}
						usePagination={true}
						pageSize={5}
						loading={false}
						filterColumn="action"
						hiddenColumns={["metadata"]}
					/>
				</CardContent>
			</Card>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/user/security")({
	component: Security,
});
/* istanbul ignore end */
